'use client'

import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
    Aperture,
    Bot,
    CheckCircle2,
    Clock3,
    Heart,
    Loader2,
    MessageSquare,
    RefreshCw,
    Save,
    SlidersHorizontal,
    Sparkles,
    X,
    XCircle,
} from 'lucide-react'
import type {Account, Contact, ContactLabel} from '@/types'
import {apiGet, apiPost, getAuthToken} from '@/lib/api'
import {displayName} from '@/lib/utils'
import {LazyAvatar} from '@/components/LazyAvatar'
import {ContactSelectorPanel} from '@/components/ContactSelectorPanel'
import {PageHeader} from '@/components/PageHeader'
import {Switch} from '@/components/Switch'
import {message} from '@/components/Toast'

type MomentMedia = {type: 'image' | 'video'; url: string; thumbUrl: string; key?: string; thumbKey?: string}
type MomentComment = {author: string; content: string; commentId?: number}
type Moment = {
    momentId: string
    authorWxid: string
    authorName: string
    content: string
    media: MomentMedia[]
    comments: MomentComment[]
    createdAt: number
    liked: boolean
    likeCount: number
}
type MomentsAutomation = {
    enabled: boolean
    autoLike: boolean
    autoComment: boolean
    commentMode: 'preset' | 'ai'
    presetTexts: string
    aiPrompt: string
    targetWxids: string[]
    targetLabelIds: number[]
    syncIntervalMinutes: number
}
type MomentAction = {
    id: number
    momentId: string
    actionType: 'like' | 'unlike' | 'comment'
    source: 'manual' | 'auto'
    content: string
    status: 'success' | 'failed'
    error: string
    createdAt: number
}
type MomentsViewProps = {
    account: Account | null
    contacts: Contact[]
    labels: ContactLabel[]
    resolveContact: (wxid: string) => Promise<Contact | null>
}

const defaultSettings: MomentsAutomation = {
    enabled: false,
    autoLike: true,
    autoComment: false,
    commentMode: 'preset',
    presetTexts: '太棒了！\n支持一下！\n厉害厉害\n为你点赞！',
    aiPrompt: '请根据朋友圈内容生成一句自然、友好的简短评论，不要使用夸张表达，不要超过 30 个字。',
    targetWxids: [],
    targetLabelIds: [],
    syncIntervalMinutes: 10,
}

function formatMomentTime(timestamp: number): string {
    if (!timestamp) return ''
    const diff = Date.now() - timestamp
    if (diff < 60_000) return '刚刚'
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`
    return new Date(timestamp).toLocaleString([], {month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'})
}

function momentMediaUrl(accountWxid: string, source: string, key?: string): string {
    const params = new URLSearchParams({wxid: accountWxid, url: source})
    if (key) params.set('key', key)
    const token = getAuthToken()
    if (token) params.set('access_token', token)
    return `/api/moments/media?${params.toString()}`
}

function objectFrom(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : {}
}

function parseJSONList(value: unknown): unknown[] {
    if (Array.isArray(value)) return value
    if (typeof value !== 'string' || !value.trim() || value.trim() === 'null') return []
    try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

function stringValue(value: unknown): string {
    if (typeof value === 'string') return value
    if (typeof value === 'number') return String(value)
    return ''
}

function normalizeMomentMedia(value: unknown): MomentMedia | null {
    const raw = objectFrom(value)
    const urlObject = objectFrom(raw.url ?? raw.URL)
    const thumbObject = objectFrom(raw.thumbUrl ?? raw.ThumbUrl ?? raw.thumb ?? raw.Thumb)
    const url = stringValue(raw.url ?? raw.URL) || stringValue(urlObject.value ?? urlObject.Value)
    const thumbUrl = stringValue(raw.thumbUrl ?? raw.ThumbUrl) || stringValue(thumbObject.value ?? thumbObject.Value)
    if (!url && !thumbUrl) return null

    const rawType = raw.type ?? raw.Type
    const typeText = String(rawType ?? '').toLowerCase()
    const type: MomentMedia['type'] = typeText === 'video' || typeText === '6' || typeText === '4'
        ? 'video'
        : 'image'
    const enc = objectFrom(raw.enc ?? raw.Enc)
    const key = stringValue(enc.key ?? enc.Key)
        || stringValue(urlObject.key ?? urlObject.Key ?? urlObject.md5 ?? urlObject.MD5)
        || stringValue(urlObject.videoMD5 ?? urlObject.VideoMD5)
    const thumbKey = stringValue(thumbObject.key ?? thumbObject.Key ?? thumbObject.md5 ?? thumbObject.MD5) || key
    return {type, url, thumbUrl, key, thumbKey}
}

function normalizeMoment(raw: unknown): Moment {
    const value = objectFrom(raw)
    const media = parseJSONList(value.media ?? value.Media ?? value.media_json ?? value.MediaJSON)
        .map(normalizeMomentMedia)
        .filter((item): item is MomentMedia => item !== null)
    const comments = parseJSONList(value.comments ?? value.Comments ?? value.comments_json ?? value.CommentsJSON)
        .map((item) => {
            const comment = objectFrom(item)
            return {
                author: stringValue(comment.author ?? comment.Author ?? comment.nickname ?? comment.NickName),
                content: stringValue(comment.content ?? comment.Content ?? comment.comment ?? comment.Comment),
                commentId: typeof (comment.commentId ?? comment.CommentID) === 'number'
                    ? comment.commentId as number
                    : undefined,
            }
        })
    const likedValue = value.liked ?? value.Liked
    return {
        momentId: stringValue(value.momentId ?? value.MomentID ?? value.moment_id),
        authorWxid: stringValue(value.authorWxid ?? value.AuthorWxid ?? value.author_wxid),
        authorName: stringValue(value.authorName ?? value.AuthorName ?? value.author_name),
        content: stringValue(value.content ?? value.Content),
        media,
        comments,
        createdAt: Number(value.createdAt ?? value.CreatedAt ?? value.created_at ?? 0),
        liked: likedValue === true || likedValue === 1 || likedValue === '1',
        likeCount: Number(value.likeCount ?? value.LikeCount ?? value.like_count ?? 0),
    }
}

function timelinePageCursor(response: unknown): {firstPageMD5: string; maxID: string; itemCount: number} {
    const root = objectFrom(response)
    const data = objectFrom(root.Data ?? root.data)
    const objectList = data.objectList ?? data.ObjectList
    const items = Array.isArray(objectList) ? objectList : []
    const lastItem = objectFrom(items.at(-1))
    return {
        firstPageMD5: stringValue(data.firstPageMd5 ?? data.FirstPageMD5),
        maxID: stringValue(lastItem.id ?? lastItem.ID),
        itemCount: items.length,
    }
}

function mergeMoments(current: Moment[], incoming: Moment[]): Moment[] {
    const seen = new Set(current.map((moment) => moment.momentId))
    return [...current, ...incoming.filter((moment) => !seen.has(moment.momentId))]
}

export function MomentsView({account, contacts, labels, resolveContact}: MomentsViewProps) {
    const [activeTab, setActiveTab] = useState<'timeline' | 'settings'>('timeline')
    const [moments, setMoments] = useState<Moment[]>([])
    const [settings, setSettings] = useState<MomentsAutomation>(defaultSettings)
    const [actions, setActions] = useState<MomentAction[]>([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [syncing, setSyncing] = useState(false)
    const [actingId, setActingId] = useState('')
    const [commentingId, setCommentingId] = useState('')
    const [commentText, setCommentText] = useState('')
    const [previewImage, setPreviewImage] = useState('')
    const [firstPageMD5, setFirstPageMD5] = useState('')
    const [nextMaxID, setNextMaxID] = useState('')
    const [hasMore, setHasMore] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const loadingMoreRef = useRef(false)
    const timelineScrollRef = useRef<HTMLDivElement>(null)
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null)
    const accountWxid = account?.wxid || ''

    const contactMap = useMemo(() => new Map(contacts.map((contact) => [contact.wxid, contact])), [contacts])

    useEffect(() => {
        const missingEnterpriseAuthors = [...new Set(moments
            .map((moment) => moment.authorWxid)
            .filter((wxid) => wxid.endsWith('@openim') && !contactMap.get(wxid)?.avatar))]
        for (const wxid of missingEnterpriseAuthors) {
            void resolveContact(wxid)
        }
    }, [contactMap, moments, resolveContact])

    useEffect(() => {
        if (!account) return
        let active = true
        setFirstPageMD5('')
        setNextMaxID('')
        setHasMore(false)
        setLoading(true)
        Promise.all([
            apiPost<{moments: Moment[]; response: unknown}>('/moments/sync', {
                wxid: account.wxid,
                FirstPageMD5: '',
                MaxID: 0,
            }, {silent: true}),
            apiGet<{settings: MomentsAutomation}>(`/moments/settings?wxid=${encodeURIComponent(account.wxid)}`),
            apiGet<{actions: MomentAction[]}>(`/moments/actions?wxid=${encodeURIComponent(account.wxid)}`),
        ]).then(([momentsResponse, settingsResponse, actionsResponse]) => {
            if (!active) return
            const cursor = timelinePageCursor(momentsResponse.response)
            setMoments((momentsResponse.moments || []).map(normalizeMoment))
            setFirstPageMD5(cursor.firstPageMD5)
            setNextMaxID(cursor.maxID)
            setHasMore(cursor.itemCount > 0 && Boolean(cursor.maxID))
            setSettings({...defaultSettings, ...settingsResponse.settings})
            setActions(actionsResponse.actions || [])
        }).catch(() => undefined).finally(() => {
            if (active) setLoading(false)
        })
        return () => {
            active = false
        }
    }, [account])

    const loadMore = useCallback(async () => {
        if (!accountWxid || !hasMore || !nextMaxID || loadingMoreRef.current) return
        loadingMoreRef.current = true
        setLoadingMore(true)
        const requestedMaxID = nextMaxID
        try {
            const response = await apiPost<{moments: Moment[]; response: unknown}>('/moments/sync', {
                wxid: accountWxid,
                FirstPageMD5: firstPageMD5,
                MaxID: requestedMaxID,
            }, {silent: true})
            const incoming = (response.moments || []).map(normalizeMoment)
            const cursor = timelinePageCursor(response.response)
            setMoments((current) => mergeMoments(current, incoming))
            if (cursor.firstPageMD5) setFirstPageMD5(cursor.firstPageMD5)
            setNextMaxID(cursor.maxID)
            setHasMore(cursor.itemCount > 0 && Boolean(cursor.maxID) && cursor.maxID !== requestedMaxID)
        } catch (error) {
            setHasMore(false)
            message.error(error instanceof Error ? error.message : '加载更多朋友圈失败')
        } finally {
            loadingMoreRef.current = false
            setLoadingMore(false)
        }
    }, [accountWxid, firstPageMD5, hasMore, nextMaxID])

    useEffect(() => {
        if (activeTab !== 'timeline' || !hasMore) return
        const target = loadMoreSentinelRef.current
        const root = timelineScrollRef.current
        if (!target || !root) return
        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) void loadMore()
        }, {root, rootMargin: '240px 0px'})
        observer.observe(target)
        return () => observer.disconnect()
    }, [activeTab, hasMore, loadMore])

    if (!account) {
        return <div className="flex h-full items-center justify-center text-sm text-muted">请先登录微信账号</div>
    }

    function updateSettings(patch: Partial<MomentsAutomation>) {
        setSettings((current) => ({...current, ...patch}))
    }

    async function refreshActions() {
        const response = await apiGet<{actions: MomentAction[]}>(`/moments/actions?wxid=${encodeURIComponent(accountWxid)}`)
        setActions(response.actions || [])
    }

    async function syncNow() {
        setSyncing(true)
        try {
            const response = await apiPost<{moments: Moment[]; inserted: number; baseline: boolean; response: unknown}>('/moments/sync', {
                wxid: accountWxid,
                FirstPageMD5: '',
                MaxID: 0,
            })
            const cursor = timelinePageCursor(response.response)
            setMoments((response.moments || []).map(normalizeMoment))
            setFirstPageMD5(cursor.firstPageMD5)
            setNextMaxID(cursor.maxID)
            setHasMore(cursor.itemCount > 0 && Boolean(cursor.maxID))
            await refreshActions()
            message.success(response.baseline ? '朋友圈基线同步完成，历史动态不会触发自动互动' : `同步完成，发现 ${response.inserted || 0} 条新动态`)
        } finally {
            setSyncing(false)
        }
    }

    async function saveSettings() {
        if (settings.enabled && settings.targetWxids.length === 0 && settings.targetLabelIds.length === 0) {
            message.error('启用自动互动前，请至少选择一个好友或标签')
            return
        }
        setSaving(true)
        try {
            const response = await apiPost<{settings: MomentsAutomation}>('/moments/settings', {...settings, wxid: accountWxid})
            setSettings({...defaultSettings, ...response.settings})
            message.success('朋友圈自动化配置已保存')
        } finally {
            setSaving(false)
        }
    }

    async function runAction(momentId: string, action: 'like' | 'unlike' | 'comment', content = '') {
        const moment = moments.find((item) => item.momentId === momentId)
        if (!moment) return
        if (action === 'unlike') {
            message.warning('当前协议文档未提供取消点赞操作')
            return
        }
        setActingId(momentId)
        try {
            await apiPost('/moments/action', {
                wxid: accountWxid,
                momentId,
                targetWxid: moment.authorWxid,
                action,
                content,
            })
            setMoments((current) => current.map((item) => item.momentId === momentId
                ? {
                    ...item,
                    liked: action === 'like' ? true : item.liked,
                    likeCount: action === 'like' && !item.liked ? item.likeCount + 1 : item.likeCount,
                    comments: action === 'comment' ? [...item.comments, {author: '我', content}] : item.comments,
                }
                : item))
            await refreshActions()
            if (action === 'comment') {
                setCommentingId('')
                setCommentText('')
            }
        } finally {
            setActingId('')
        }
    }

    return (
        <div className="flex h-full flex-col bg-page">
            <PageHeader
                title="朋友圈助手"
                description="浏览好友动态，并配置自动点赞与智能评论策略"
                icon={<Aperture size={18}/>}
                action={activeTab === 'timeline' ? (
                    <button type="button" onClick={syncNow} disabled={syncing || loadingMore} className="flex items-center gap-1.5 rounded border border-hair bg-white px-3 py-1.5 text-sm text-ink hover:border-accent hover:text-accent disabled:opacity-50">
                        <RefreshCw size={14} className={syncing ? 'animate-spin' : ''}/> 同步动态
                    </button>
                ) : undefined}
            />
            <div className="flex h-11 shrink-0 items-end gap-6 border-b border-hair px-6">
                <button type="button" onClick={() => setActiveTab('timeline')} className={`h-11 border-b-2 text-sm font-medium ${activeTab === 'timeline' ? 'border-accent text-accent' : 'border-transparent text-soft'}`}>朋友圈大厅</button>
                <button type="button" onClick={() => setActiveTab('settings')} className={`h-11 border-b-2 text-sm font-medium ${activeTab === 'settings' ? 'border-accent text-accent' : 'border-transparent text-soft'}`}>自动化互动配置</button>
            </div>

            {activeTab === 'timeline' ? (
                <div
                    ref={timelineScrollRef}
                    onScroll={(event) => {
                        const element = event.currentTarget
                        if (element.scrollHeight - element.scrollTop - element.clientHeight <= 240) void loadMore()
                    }}
                    className="flex-1 overflow-y-auto p-6"
                >
                    <div className="mx-auto max-w-2xl space-y-4 pb-8">
                        <div className="overflow-hidden rounded-card border border-hair bg-white shadow-card">
                            <div className="relative h-32 bg-gradient-to-br from-[#667eea] to-[#764ba2]">
                                <div className="absolute -bottom-4 right-5 flex max-w-[calc(100%-2.5rem)] items-center gap-3">
                                    <div className="min-w-0 truncate text-base font-medium text-white">{account.nickName || account.wxid}</div>
                                    <LazyAvatar src={account.avatar} seed={account.wxid} alt={account.nickName} className="h-16 w-16 shrink-0 rounded-lg border-2 border-white bg-gray-100 shadow"/>
                                </div>
                            </div>
                            <div className="h-10"/>
                        </div>

                        {loading ? (
                            <div className="flex justify-center py-16 text-muted"><Loader2 size={24} className="animate-spin"/></div>
                        ) : moments.length === 0 ? (
                            <div className="rounded-card border border-dashed border-hair bg-white py-16 text-center text-sm text-muted">暂无朋友圈缓存，点击“同步动态”获取最新内容</div>
                        ) : moments.map((moment) => {
                            const contact = contactMap.get(moment.authorWxid)
                            const author = contact ? displayName(contact) : moment.authorName || moment.authorWxid
                            return (
                                <article key={moment.momentId} className="flex rounded-card border border-hair bg-white p-5 shadow-card">
                                    <LazyAvatar src={contact?.avatar} seed={moment.authorWxid || author} alt={author} className="mr-4 h-10 w-10 shrink-0 rounded bg-gray-100"/>
                                    <div className="min-w-0 flex-1">
                                        <div className="mb-1.5 text-[15px] font-medium text-[#576b95]">{author}</div>
                                        {moment.content ? <p className="mb-3 whitespace-pre-wrap break-words text-[15px] leading-relaxed text-ink">{moment.content}</p> : null}
                                        {moment.media.length > 0 ? (
                                            <div className="mb-3 space-y-2">
                                                {moment.media.some((media) => media.type === 'image') ? (
                                                    <div className="grid max-w-md grid-cols-3 gap-1.5">
                                                        {moment.media.filter((media) => media.type === 'image').map((media, index) => (
                                                        <button key={`${media.url}-${index}`} type="button" onClick={() => setPreviewImage(momentMediaUrl(accountWxid, media.url || media.thumbUrl, media.key || media.thumbKey))} className="relative aspect-square overflow-hidden rounded bg-gray-100">
                                                            <img
                                                                src={momentMediaUrl(accountWxid, media.thumbUrl || media.url, media.thumbKey || media.key)}
                                                                alt=""
                                                                className="h-full w-full object-cover"
                                                            />
                                                        </button>
                                                        ))}
                                                    </div>
                                                ) : null}
                                                {moment.media.filter((media) => media.type === 'video').map((media, index) => (
                                                    <video key={`${media.url}-${index}`} src={momentMediaUrl(accountWxid, media.url, media.key)} poster={momentMediaUrl(accountWxid, media.thumbUrl, media.thumbKey || media.key)} controls preload="metadata" className="max-h-[420px] w-full max-w-xl rounded-lg bg-black object-contain"/>
                                                ))}
                                            </div>
                                        ) : null}
                                        <div className="flex items-center justify-between text-xs text-muted">
                                            <span>{formatMomentTime(moment.createdAt)}</span>
                                            <div className="flex gap-1">
                                                <button type="button" disabled={actingId === moment.momentId} onClick={() => runAction(moment.momentId, moment.liked ? 'unlike' : 'like')} title={moment.liked ? '取消点赞' : '点赞'} className={`rounded p-1.5 hover:bg-page ${moment.liked ? 'text-red-500' : 'text-soft'}`}>
                                                    <Heart size={16} className={moment.liked ? 'fill-current' : ''}/>
                                                </button>
                                                <button type="button" onClick={() => setCommentingId(commentingId === moment.momentId ? '' : moment.momentId)} title="评论" className="rounded p-1.5 text-soft hover:bg-page"><MessageSquare size={16}/></button>
                                            </div>
                                        </div>
                                        {(moment.likeCount > 0 || moment.comments.length > 0) ? (
                                            <div className="mt-3 space-y-1.5 rounded bg-page p-3 text-sm">
                                                {moment.likeCount > 0 ? <div className={`flex items-center gap-2 text-[#576b95] ${moment.comments.length > 0 ? 'border-b border-hair pb-2' : 'h-5'}`}><Heart size={13}/> {moment.likeCount} 人点赞</div> : null}
                                                {moment.comments.map((comment, index) => <div key={`${comment.commentId || index}-${comment.author}`}><span className="font-medium text-[#576b95]">{comment.author}</span><span className="text-soft">: </span><span className="text-ink">{comment.content}</span></div>)}
                                            </div>
                                        ) : null}
                                        {commentingId === moment.momentId ? (
                                            <div className="mt-3 flex gap-2">
                                                <input value={commentText} onChange={(event) => setCommentText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && commentText.trim()) void runAction(moment.momentId, 'comment', commentText.trim()) }} autoFocus placeholder="输入评论内容" className="h-9 flex-1 rounded border border-hair px-3 text-sm outline-none focus:border-accent"/>
                                                <button type="button" disabled={!commentText.trim() || actingId === moment.momentId} onClick={() => runAction(moment.momentId, 'comment', commentText.trim())} className="rounded bg-accent px-3 text-sm text-white disabled:opacity-50">发送</button>
                                            </div>
                                        ) : null}
                                    </div>
                                </article>
                            )
                        })}
                        {moments.length > 0 ? (
                            <div ref={loadMoreSentinelRef} className="flex min-h-12 items-center justify-center py-3 text-xs text-muted">
                                {loadingMore ? <><Loader2 size={15} className="mr-2 animate-spin"/> 正在加载更多...</> : hasMore ? '继续下滑加载更多' : '没有更多动态了'}
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : (
                <div className="flex flex-1 gap-6 overflow-hidden p-6">
                    <ContactSelectorPanel
                        contacts={contacts}
                        labels={labels}
                        selectedWxids={settings.targetWxids}
                        selectedLabelIds={settings.targetLabelIds}
                        onSelect={(wxid) => updateSettings({targetWxids: settings.targetWxids.includes(wxid) ? settings.targetWxids : [...settings.targetWxids, wxid]})}
                        onRemove={(wxid) => updateSettings({targetWxids: settings.targetWxids.filter((value) => value !== wxid)})}
                        onLabelSelect={(id) => updateSettings({targetLabelIds: settings.targetLabelIds.includes(id) ? settings.targetLabelIds : [...settings.targetLabelIds, id]})}
                        onLabelRemove={(id) => updateSettings({targetLabelIds: settings.targetLabelIds.filter((value) => value !== id)})}
                        onClear={() => updateSettings({targetWxids: [], targetLabelIds: []})}
                        emptyHint="请选择自动互动生效的好友或标签"
                        placeholder="搜索并添加..."
                    />

                    <div className="flex h-full flex-1 flex-col overflow-hidden rounded-card border border-hair bg-white shadow-card">
                        <div className="flex items-center justify-between border-b border-hair bg-gray-50 p-4">
                            <div>
                                <h3 className="flex items-center gap-2 text-sm font-medium text-ink"><SlidersHorizontal size={16} className="text-accent"/> 自动化规则设置</h3>
                                <p className="mt-1 text-xs text-muted">首次同步仅建立基线，后续新增动态才会自动互动。</p>
                            </div>
                            <Switch checked={settings.enabled} onChange={() => updateSettings({enabled: !settings.enabled})}/>
                        </div>
                        <div className="flex-1 space-y-5 overflow-y-auto p-5">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-soft">已选对象：</span>
                                <span className="text-xs font-medium text-accent">好友 {settings.targetWxids.length} 人，标签 {settings.targetLabelIds.length} 个</span>
                            </div>
                            <div className="grid gap-4 md:grid-cols-2">
                                <SettingSwitch title="自动点赞" description="所选对象发布新朋友圈后，自动点赞" checked={settings.autoLike} onChange={() => updateSettings({autoLike: !settings.autoLike})}/>
                                <SettingSwitch title="自动评论" description="所选对象发布新朋友圈后，自动生成并发送评论" checked={settings.autoComment} onChange={() => updateSettings({autoComment: !settings.autoComment})}/>
                            </div>
                            <div className={`rounded-card border border-hair p-4 ${settings.autoComment ? '' : 'pointer-events-none opacity-50'}`}>
                                <div className="mb-3 text-sm font-medium text-ink">评论生成方式</div>
                                <div className="mb-4 flex flex-wrap gap-4 text-sm">
                                    <label className="flex cursor-pointer items-center gap-2"><input type="radio" checked={settings.commentMode === 'preset'} onChange={() => updateSettings({commentMode: 'preset'})}/> 随机抽取预设话术</label>
                                    <label className="flex cursor-pointer items-center gap-2"><input type="radio" checked={settings.commentMode === 'ai'} onChange={() => updateSettings({commentMode: 'ai'})}/> <Sparkles size={14} className="text-indigo-500"/> AI 智能解析生成</label>
                                </div>
                                {settings.commentMode === 'preset' ? (
                                    <textarea rows={5} value={settings.presetTexts} onChange={(event) => updateSettings({presetTexts: event.target.value})} placeholder="每行一条评论话术" className="w-full rounded border border-hair p-3 text-sm outline-none focus:border-accent"/>
                                ) : (
                                    <div><div className="mb-2 flex items-center gap-2 text-xs text-muted"><Bot size={14}/> AI 将结合朋友圈正文生成简短评论，可自定义提示词。</div><textarea rows={5} value={settings.aiPrompt} onChange={(event) => updateSettings({aiPrompt: event.target.value})} placeholder="输入 AI 评论提示词" className="w-full rounded border border-hair p-3 text-sm outline-none focus:border-accent"/></div>
                                )}
                            </div>
                            <div className="flex items-center justify-between rounded-card border border-hair p-4">
                                <div><h4 className="flex items-center gap-2 text-sm font-medium text-ink"><Clock3 size={16} className="text-accent"/> 数据同步间隔</h4><p className="mt-1 text-xs text-muted">后台扫描新动态，范围 1 至 1440 分钟。</p></div>
                                <div className="flex items-center gap-2"><input type="number" min={1} max={1440} value={settings.syncIntervalMinutes} onChange={(event) => updateSettings({syncIntervalMinutes: Number(event.target.value)})} className="h-9 w-20 rounded border border-hair px-3 text-sm outline-none focus:border-accent"/><span className="text-sm text-soft">分钟</span></div>
                            </div>
                        </div>
                        <div className="flex justify-end border-t border-hair bg-gray-50 p-4">
                            <button type="button" disabled={saving} onClick={saveSettings} className="flex items-center gap-1.5 rounded bg-accent px-4 py-2 text-sm text-white hover:bg-accent-hover disabled:opacity-50">{saving ? <Loader2 size={15} className="animate-spin"/> : <Save size={15}/>} 保存配置</button>
                        </div>
                    </div>

                    <div className="flex h-full w-[320px] shrink-0 flex-col overflow-hidden rounded-card border border-hair bg-white shadow-card">
                        <div className="flex items-center justify-between border-b border-hair bg-gray-50 p-4">
                            <h3 className="text-sm font-medium text-ink">执行日志 ({actions.length})</h3>
                            <button type="button" onClick={() => void refreshActions()} className="rounded p-1 text-muted hover:bg-white hover:text-accent" title="刷新执行日志"><RefreshCw size={15}/></button>
                        </div>
                        <div className="flex-1 space-y-3 overflow-auto p-3">
                            {actions.length ? actions.map((action) => {
                                const meta = actionMeta(action)
                                const moment = moments.find((item) => item.momentId === action.momentId)
                                const contact = moment ? contactMap.get(moment.authorWxid) : undefined
                                const author = contact ? displayName(contact) : moment?.authorName
                                return (
                                    <div key={action.id} className="space-y-2 rounded-lg border border-hair bg-white p-3">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="flex items-center gap-1.5 text-sm font-medium text-ink">{meta.icon} {meta.label}</span>
                                            <span className={`inline-flex h-5 items-center rounded px-1.5 text-[11px] ${meta.statusClass}`}>{action.status === 'success' ? '成功' : '失败'}</span>
                                        </div>
                                        <div className="text-xs text-muted">{new Date(action.createdAt).toLocaleString()} · {action.source === 'auto' ? '自动执行' : '手动操作'}</div>
                                        {author ? <div className="truncate text-xs text-ink" title={author}>发布人：{author}</div> : null}
                                        <div className="truncate text-xs text-soft" title={action.momentId}>动态 ID：{action.momentId}</div>
                                        {action.content ? <div className="line-clamp-2 text-xs text-ink" title={action.content}>评论：{action.content}</div> : null}
                                        {action.error ? <div className="line-clamp-2 text-[11px] text-red-500" title={action.error}>失败：{action.error}</div> : null}
                                    </div>
                                )
                            }) : <div className="py-12 text-center text-sm text-muted">暂无执行日志</div>}
                        </div>
                    </div>
                </div>
            )}
            {previewImage ? (
                <button type="button" onClick={() => setPreviewImage('')} aria-label="关闭朋友圈图片预览" className="fixed inset-0 z-[100] flex cursor-zoom-out items-center justify-center bg-black/55 p-10">
                    <img src={previewImage} alt="朋友圈图片预览" className="max-h-[82vh] max-w-[82vw] rounded-lg bg-white object-contain shadow-2xl"/>
                    <span className="absolute right-6 top-6 rounded-full bg-black/50 p-2 text-white"><X size={22}/></span>
                </button>
            ) : null}
        </div>
    )
}

function SettingSwitch({title, description, checked, onChange}: {title: string; description: string; checked: boolean; onChange: () => void}) {
    return <div className="flex items-center justify-between rounded-card border border-hair p-4"><div><h4 className="text-sm font-medium text-ink">{title}</h4><p className="mt-1 text-xs text-muted">{description}</p></div><Switch checked={checked} onChange={onChange}/></div>
}

function actionMeta(action: MomentAction) {
    const statusClass = action.status === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
    const icon = action.status === 'success' ? <CheckCircle2 size={14} className="text-green-500"/> : <XCircle size={14} className="text-red-500"/>
    const label = action.actionType === 'like' ? '点赞' : action.actionType === 'unlike' ? '取消点赞' : '评论'
    return {statusClass, icon, label}
}
